import { FormGroup, FormParagraph, Heading, List } from '@ksyos/design-system';
import type { ConclusieDiagnoseOption } from '@common/enums';
import type { IDiagnosis } from '@frontend/components/interfaces';
import { getSelectedLanguage } from '@frontend/translate';
import { DisplayIcd10 } from './DisplayIcd10';

type Icd10SelectionReadProps = {
    diagnosis?: IDiagnosis;
    diagnosisConclusion?: ConclusieDiagnoseOption;
    hideTitle?: boolean;
    hideWorkingDiagnosisLabel?: boolean;
    onContentLoaded?: (hasContent: boolean) => Promise<void>;
    showWorkingDiagnosis?: boolean;
    thesaurusTermDate?: string | null;
};

export function Icd10SelectionsRead({
    diagnosis,
    diagnosisConclusion,
    hideTitle,
    hideWorkingDiagnosisLabel,
    onContentLoaded,
    showWorkingDiagnosis,
    thesaurusTermDate,
}: Icd10SelectionReadProps) {
    const {
        diagnosis: { workingDiagnosis, differentialDiagnosis },
    } = getSelectedLanguage();

    const {
        considerationThesaurusTermId,
        diagnosisConsiderationIcd10,

        diagnosisDifferentialConsiderationIcd10a,
        diagnosisDifferentialConsiderationIcd10b,
        diagnosisDifferentialConsiderationIcd10c,
        differentialConsiderationThesaurusTermIda,
        differentialConsiderationThesaurusTermIdb,
        differentialConsiderationThesaurusTermIdc,
    } = diagnosis || {};

    const diagnosisDifferentialConsiderations = [
        {
            icd10Code: diagnosisDifferentialConsiderationIcd10a,
            thesaurusTermId: differentialConsiderationThesaurusTermIda,
        },
        {
            icd10Code: diagnosisDifferentialConsiderationIcd10b,
            thesaurusTermId: differentialConsiderationThesaurusTermIdb,
        },
        {
            icd10Code: diagnosisDifferentialConsiderationIcd10c,
            thesaurusTermId: differentialConsiderationThesaurusTermIdc,
        },
    ];

    const shouldDisplayHeading =
        !hideTitle &&
        ((diagnosisConsiderationIcd10 && showWorkingDiagnosis) ||
            diagnosisDifferentialConsiderationIcd10a ||
            diagnosisDifferentialConsiderationIcd10b ||
            diagnosisDifferentialConsiderationIcd10c);

    const shouldDisplayDiagnosisDetails =
        (diagnosisConsiderationIcd10 || considerationThesaurusTermId) && showWorkingDiagnosis;

    const shouldDisplayDiagnosisConclusion =
        !(diagnosisConsiderationIcd10 || considerationThesaurusTermId) && showWorkingDiagnosis && diagnosisConclusion;

    const hasDifferentialConsiderations = diagnosisDifferentialConsiderations.some(
        (diagnosisDifferentialConsideration) => diagnosisDifferentialConsideration.icd10Code
    );

    return (
        <>
            {shouldDisplayHeading && <Heading size="small">{workingDiagnosis}</Heading>}

            {shouldDisplayDiagnosisDetails && (
                <FormGroup label={hideWorkingDiagnosisLabel ? '' : workingDiagnosis}>
                    <FormParagraph>
                        <DisplayIcd10
                            icd10Code={diagnosisConsiderationIcd10 || null}
                            onContentLoaded={onContentLoaded}
                            thesaurusTermDate={thesaurusTermDate}
                            thesaurusTermId={considerationThesaurusTermId}
                        />
                    </FormParagraph>
                </FormGroup>
            )}

            {shouldDisplayDiagnosisConclusion && (
                <FormGroup label={workingDiagnosis}>
                    <FormParagraph>{diagnosisConclusion}</FormParagraph>
                </FormGroup>
            )}

            {hasDifferentialConsiderations && (
                <FormGroup label={differentialDiagnosis}>
                    <List>
                        {diagnosisDifferentialConsiderations.map((item, index) => (
                            <List.Item key={`${item.icd10Code || item.thesaurusTermId}-${index}`}>
                                <FormParagraph>
                                    <DisplayIcd10
                                        icd10Code={item.icd10Code || null}
                                        onContentLoaded={onContentLoaded}
                                        thesaurusTermDate={thesaurusTermDate}
                                        thesaurusTermId={item.thesaurusTermId}
                                    />
                                </FormParagraph>
                            </List.Item>
                        ))}
                    </List>
                </FormGroup>
            )}
        </>
    );
}
